// memberDetail/styled.js
import { styled } from '@mui/material/styles';
import { Dialog, DialogContent, Box, Paper } from '@mui/material';

const honeyColors = {
  primary: '#F1C40F',
  primaryLight: '#F4D03F',
  primaryDark: '#D4AC0D',
  amber: '#FFB74D',
  cream: '#FFF8E1',
  honeycomb: '#D4A574',
  surface: '#FFFFFF',
  surfaceHoney: '#FFF9E6',
  surfaceGlass: 'rgba(255, 255, 255, 0.85)',
  border: '#F5E6B3',
  borderLight: '#FAF0C4',
  textPrimary: '#3E2723',
  textSecondary: '#5D4037',
  textMuted: '#8D6E63',
  error: '#EF5350',
  gradientPrimary: 'linear-gradient(135deg, #F1C40F 0%, #FFB74D 100%)',
  gradientHoneycomb: 'linear-gradient(135deg, #D4A574 0%, #F1C40F 50%, #FFB74D 100%)',
  gradientCard: 'linear-gradient(145deg, rgba(255,255,255,0.9) 0%, rgba(255,249,230,0.8) 100%)',
  shadowLight: '0 2px 8px rgba(212, 165, 116, 0.12)',
  shadowMedium: '0 4px 12px rgba(212, 165, 116, 0.18)',
  shadowHeavy: '0 8px 24px rgba(212, 165, 116, 0.25)',
  backdropBlur: 'blur(20px)',
};

export const StyledDialog = styled(Dialog)({
  '& .MuiDialog-paper': {
    borderRadius: '24px',
    background: honeyColors.gradientCard,
    border: `2px solid ${honeyColors.border}`,
    boxShadow: honeyColors.shadowHeavy,
    backdropFilter: honeyColors.backdropBlur,
    overflow: 'hidden',
    maxHeight: '90vh',
    
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: '6px',
      background: honeyColors.gradientPrimary,
      borderRadius: '22px 22px 0 0',
    },
  },
  
  '& .MuiBackdrop-root': {
    backgroundColor: 'rgba(62, 39, 35, 0.4)',
    backdropFilter: 'blur(8px)',
  },
});

export const StyledDialogContent = styled(DialogContent)({
  background: 'transparent',
  padding: '0 32px 32px',
  overflow: 'auto',
  
  '&::-webkit-scrollbar': {
    width: '8px',
  },
  
  '&::-webkit-scrollbar-track': {
    background: honeyColors.surfaceHoney,
    borderRadius: '4px',
  },
  
  '&::-webkit-scrollbar-thumb': {
    background: honeyColors.border,
    borderRadius: '4px',
    
    '&:hover': {
      background: honeyColors.primary,
    },
  },
});

export const MemberDetailHeader = styled(Box)({
  display: 'flex',
  alignItems: 'flex-start',
  padding: '32px 32px 24px',
  borderBottom: `2px solid ${honeyColors.borderLight}`,
  background: 'transparent',
  
  '& .MuiTypography-h4': {
    color: honeyColors.textPrimary,
    fontWeight: 700,
  },
  
  '& .MuiTypography-h6': {
    color: honeyColors.textSecondary,
    fontStyle: 'italic',
  },
});

export const MemberDetailContent = styled(Box)({
  paddingTop: '32px',
});

export const MemberAvatarContainer = styled(Box)({
  position: 'relative',
  display: 'inline-block',
  
  '& .MuiAvatar-root': {
    border: `4px solid ${honeyColors.surface}`,
    boxShadow: honeyColors.shadowMedium,
    transition: 'all 0.3s ease',
  },
});

export const InfoSection = styled(Paper)({
  padding: '24px',
  borderRadius: '20px',
  background: honeyColors.surfaceGlass,
  border: `2px solid ${honeyColors.borderLight}`,
  boxShadow: honeyColors.shadowLight,
  height: '100%',
  
  '& .MuiTypography-h6': {
    color: honeyColors.textPrimary,
    borderBottom: `2px solid ${honeyColors.borderLight}`,
    paddingBottom: '8px',
    marginBottom: '16px',
    
    '&::after': {
      content: '""',
      display: 'block',
      width: '40px',
      height: '2px',
      background: honeyColors.gradientPrimary,
      marginTop: '4px',
    },
  },
  
  '& .MuiTypography-body1': {
    color: honeyColors.textPrimary,
    lineHeight: 1.7,
  },
  
  '& .MuiTypography-subtitle2': {
    color: honeyColors.textSecondary,
    fontWeight: 600,
  },
  
  '& .MuiTypography-body2': {
    color: honeyColors.textMuted,
  },
});

export const LinksSection = styled(InfoSection)({
  '& .MuiLink-root': {
    color: honeyColors.primary,
    textDecoration: 'none',
    fontWeight: 500,
    transition: 'all 0.2s ease',
    
    '&:hover': {
      color: honeyColors.primaryDark,
      textDecoration: 'underline',
      transform: 'translateX(4px)',
    },
    
    '& .MuiSvgIcon-root': {
      color: honeyColors.textMuted,
    },
  },
});

export const AdminActionsSection = styled(Paper)({
  padding: '24px',
  marginTop: '24px',
  borderRadius: '20px',
  background: honeyColors.surfaceGlass,
  border: `2px solid ${honeyColors.borderLight}`,
  boxShadow: honeyColors.shadowLight,
  
  '& .MuiTypography-h6': {
    color: honeyColors.textPrimary,
    borderBottom: `2px solid ${honeyColors.borderLight}`,
    paddingBottom: '8px',
    marginBottom: '16px',
    
    '&::after': {
      content: '""',
      display: 'block',
      width: '40px',
      height: '2px',
      background: honeyColors.gradientPrimary,
      marginTop: '4px',
    },
  },
  
  '& .MuiButton-outlined': {
    borderColor: honeyColors.border,
    color: honeyColors.textPrimary,
    borderRadius: '16px',
    fontWeight: 600,
    transition: 'all 0.2s ease',
    textTransform: 'none',
    padding: '10px 24px',
    
    '&:hover': {
      borderColor: honeyColors.primary,
      backgroundColor: honeyColors.surfaceHoney,
      color: honeyColors.textPrimary,
    },
    
    '&.MuiButton-colorError': {
      borderColor: honeyColors.error,
      color: honeyColors.error,
      
      '&:hover': {
        borderColor: honeyColors.error,
        backgroundColor: 'rgba(239, 83, 80, 0.1)',
      },
    },
  },
});

export const RoleChangeDialog = styled(Dialog)({
  '& .MuiDialog-paper': {
    borderRadius: '20px',
    background: honeyColors.gradientCard,
    border: `2px solid ${honeyColors.border}`,
    boxShadow: honeyColors.shadowHeavy,
    minWidth: '400px',
    
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: '4px',
      background: honeyColors.gradientPrimary,
      borderRadius: '20px 20px 0 0',
    },
  },
  
  '& .MuiDialogTitle-root': {
    padding: '24px 32px 16px',
    color: honeyColors.textPrimary,
    fontWeight: 600,
  },
  
  '& .MuiDialogContent-root': {
    padding: '0 32px 24px',
    
    '& .MuiFormControl-root': {
      marginTop: '16px',
      
      '& .MuiOutlinedInput-root': {
        borderRadius: '12px',
        backgroundColor: honeyColors.surface,
        border: `1px solid ${honeyColors.borderLight}`,
        
        '&:hover': {
          borderColor: honeyColors.border,
        },
        
        '&.Mui-focused': {
          borderColor: honeyColors.primary,
          boxShadow: `0 0 0 2px rgba(241, 196, 15, 0.1)`,
        },
        
        '& .MuiOutlinedInput-notchedOutline': {
          border: 'none',
        },
      },
      
      '& .MuiInputLabel-root': {
        color: honeyColors.textMuted,
        
        '&.Mui-focused': {
          color: honeyColors.primary,
        },
      },
    },
  },
  
  '& .MuiDialogActions-root': {
    padding: '20px 32px 32px',
    gap: '12px',
    
    '& .MuiButton-root': {
      borderRadius: '12px',
      textTransform: 'none',
      fontWeight: 600,
      padding: '10px 24px',
      
      '&.MuiButton-text': {
        color: honeyColors.textMuted,
        
        '&:hover': {
          backgroundColor: honeyColors.surfaceHoney,
          color: honeyColors.textPrimary,
        },
      },
      
      '&.MuiButton-contained': {
        background: honeyColors.gradientPrimary,
        color: honeyColors.textPrimary,
        boxShadow: honeyColors.shadowMedium,
        
        '&:hover': {
          background: honeyColors.gradientHoneycomb,
          boxShadow: honeyColors.shadowHeavy,
        },
      },
    },
  },
});

export const RemoveConfirmDialog = styled(Dialog)({
  '& .MuiDialog-paper': {
    borderRadius: '20px',
    background: honeyColors.gradientCard,
    border: `2px solid ${honeyColors.error}`,
    boxShadow: honeyColors.shadowHeavy,
    minWidth: '400px',
    
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: '4px',
      background: `linear-gradient(135deg, ${honeyColors.error} 0%, #ff7043 100%)`,
      borderRadius: '20px 20px 0 0',
    },
  },
  
  '& .MuiDialogTitle-root': {
    padding: '24px 32px 16px',
    color: honeyColors.error,
    fontWeight: 600,
  },
  
  '& .MuiDialogContent-root': {
    padding: '0 32px 24px',
    
    '& .MuiTextField-root': {
      '& .MuiOutlinedInput-root': {
        borderRadius: '12px',
        backgroundColor: honeyColors.surface,
        border: `1px solid ${honeyColors.borderLight}`,
        
        '&:hover': {
          borderColor: honeyColors.border,
        },
        
        '&.Mui-focused': {
          borderColor: honeyColors.primary,
          boxShadow: `0 0 0 2px rgba(241, 196, 15, 0.1)`,
        },
        
        '& .MuiOutlinedInput-notchedOutline': {
          border: 'none',
        },
      },
      
      '& .MuiInputLabel-root': {
        color: honeyColors.textMuted,
        
        '&.Mui-focused': {
          color: honeyColors.primary,
        },
      },
    },
  },
  
  '& .MuiDialogActions-root': {
    padding: '20px 32px 32px',
    gap: '12px',
    
    '& .MuiButton-root': {
      borderRadius: '12px',
      textTransform: 'none',
      fontWeight: 600,
      padding: '10px 24px',
      
      '&.MuiButton-text': {
        color: honeyColors.textMuted,
        
        '&:hover': {
          backgroundColor: honeyColors.surfaceHoney,
          color: honeyColors.textPrimary,
        },
      },
      
      '&.MuiButton-contained': {
        '&.MuiButton-colorError': {
          background: `linear-gradient(135deg, ${honeyColors.error} 0%, #ff7043 100%)`,
          color: honeyColors.surface,
          boxShadow: honeyColors.shadowMedium,
          
          '&:hover': {
            background: `linear-gradient(135deg, #e53935 0%, #ff5722 100%)`,
            boxShadow: honeyColors.shadowHeavy,
          },
        },
      },
    },
  },
});